/**
 * Batching policy for the replay event stream (#30070 capture slice).
 *
 * Pure and DOM-free on purpose: the flush rules are the part most likely to
 * cause data loss or a 413, and they are testable without rrweb, a timer, or a
 * network.
 *
 * The two caps mirror the ingest contract exactly
 * (`ReplayIngestOptions.MaxEventsPerBatch` = 20,000 and
 * `MaxDecompressedBytes` = 15 MB, enforced server-side as 413/400). The client
 * defaults sit well UNDER both, because the server cap is a rejection boundary
 * and the client target is a latency and memory one. A batch that only flushed at
 * the server limit would hold a user's whole session in memory and lose all of
 * it on a crash.
 */

/** Platform-neutral envelope, one per captured event. Matches `ReplayEventEnvelope`. */
export interface IRynReplayEnvelope {
  sessionId: string;
  app: string;
  platform: string;
  /** Epoch milliseconds UTC. */
  ts: number;
  /**
   * Cumulative count of events emitted in this session up to and INCLUDING this
   * one (a 1-based running index). Matches `ReplayEventEnvelope.SessionEventCount`
   * and is what makes `RynReplaySession.EventCount` an exact session total rather
   * than a per-batch high-water mark (#30110).
   *
   * Assigned by `RynReplayBatcher.add`, the one funnel every envelope passes
   * through exactly once, and carried with the envelope everywhere it then goes
   * (buffer, offline store, retry queue), so a re-sent batch carries the SAME
   * values. The server merges EventCount as MAX, and the MAX of a monotonic
   * counter is the total AND is retry-safe: a replay ties or loses the MAX, never
   * inflates. Optional only so the interface stays truthful for a caller that
   * builds an envelope by hand; the batcher always sets it.
   */
  sessionEventCount?: number;
  type: string;
  /**
   * Current page, as a plain label. TOP-LEVEL, matching
   * `ReplayEventEnvelope.Url`.
   *
   * This is the ONLY field the server interprets: it populates
   * `RynReplaySessionPage` and `RynReplaySession.LandingUrl`, and
   * `ReplaySessionIndexProjection` reads it from the envelope and is documented
   * never to open `payload`. So a url tucked inside `payload` is invisible to the
   * server, and the resulting empty page trail looks identical to a user who never
   * navigated. Optional because the server treats an absent value as "this event
   * is not navigation-ish", not as an error.
   */
  url?: string;
  /**
   * Who was using the app, as a Software Security user id (a Guid string).
   *
   * The server reads this and stores it on the session row, so it is what makes
   * "find this person's recordings" work. Absent is normal and not an error: the
   * recorder starts when the app boots, before anyone has logged in, so the
   * opening events of a real session have nobody on them. The server takes the
   * first identity it sees in the batch.
   *
   * Send a Guid or send nothing. A value that is not a Guid fails the whole
   * batch with a 400, on purpose, because this is copied off a token claim by
   * our own code and a malformed one is always a bug worth seeing.
   */
  securityUserId?: string;
  /**
   * Top-level privacy signal for `anonymize()` / `clearIdentity()`. When true,
   * ingest clears `RynReplaySession.SecurityUserId` on the index row. Must stay
   * on the envelope (not only inside `payload`) so the projection never opens
   * Payload — same rule as `url` / `securityUserId`.
   */
  anonymized?: boolean;
  /**
   * App build label stamped at capture time. Top-level so ingest projection can
   * read it without opening `payload` (same rule as `url` / `securityUserId`).
   */
  appVersion?: string;
  /** Build number / CI id paired with `appVersion`. */
  buildNumber?: string;
  /**
   * Verbatim `navigator.userAgent` (#30211). Top-level so the index can store
   * it without opening `payload`. Browser/OS parsing stays client-side in the
   * player — UA rules change too often to bake into SQL.
   */
  userAgent?: string;
  /**
   * Deploy environment name at record time (`dev` / `stg` / `prod`, …).
   * Top-level for the same projection rule as `url` / `appVersion`.
   */
  environment?: string;
  payload: Record<string, unknown>;
}

export interface IRynReplayBatchLimits {
  /** Flush once the buffer holds this many events. */
  maxEvents: number;
  /** Flush once the serialized buffer reaches this many bytes. */
  maxBytes: number;
}

/**
 * Server rejection boundaries. Exported so the client can never be configured
 * past them, and so a test asserts against the same numbers the function app
 * enforces rather than a copy that can drift.
 */
export const RYN_REPLAY_SERVER_MAX_EVENTS_PER_BATCH = 20_000;
/** Matches ingest `RynReplay:MaxDecompressedBytes` default (15 MB). */
export const RYN_REPLAY_SERVER_MAX_DECOMPRESSED_BYTES = 15 * 1024 * 1024;

/**
 * Client defaults. 500 events / 512 KB keeps a flush well inside one network
 * round trip and bounds how much of a session a browser crash can take with it.
 */
export const RYN_REPLAY_DEFAULT_BATCH_LIMITS: IRynReplayBatchLimits = {
  maxEvents: 500,
  maxBytes: 512 * 1024,
};

/**
 * Clamp requested limits into the server's rejection boundaries. Configuration
 * arrives from Azure App Configuration by hand, so a typo that asks for a
 * 50 MB batch must degrade to the server maximum rather than produce a session
 * where every flush 413s and every event is lost.
 */
export const clampRynReplayBatchLimits = (
  limits: Partial<IRynReplayBatchLimits> = {}
): IRynReplayBatchLimits => {
  const requestedEvents =
    limits.maxEvents && limits.maxEvents > 0
      ? limits.maxEvents
      : RYN_REPLAY_DEFAULT_BATCH_LIMITS.maxEvents;
  const requestedBytes =
    limits.maxBytes && limits.maxBytes > 0
      ? limits.maxBytes
      : RYN_REPLAY_DEFAULT_BATCH_LIMITS.maxBytes;

  return {
    maxEvents: Math.min(
      requestedEvents,
      RYN_REPLAY_SERVER_MAX_EVENTS_PER_BATCH
    ),
    maxBytes: Math.min(
      requestedBytes,
      RYN_REPLAY_SERVER_MAX_DECOMPRESSED_BYTES
    ),
  };
};

/** Serialized size of one envelope, the same JSON the transport will send. */
export const rynReplayEnvelopeBytes = (envelope: IRynReplayEnvelope): number =>
  JSON.stringify(envelope).length;

/**
 * Accumulates envelopes and hands back a batch the moment either cap is
 * reached. Holds no timer of its own — the caller owns the interval and the
 * unload hook, so this stays synchronous and testable.
 */
export class RynReplayBatcher {
  readonly limits: IRynReplayBatchLimits;
  private buffer: IRynReplayEnvelope[] = [];
  private bufferedBytes = 0;
  // Cumulative event counter for this session. A new batcher is constructed per
  // session (the recorder does `new RynReplayBatcher(...)` in start()), so this
  // resets for free and stays a true session running-total across pause/resume.
  private sessionEventCount = 0;

  constructor(limits: Partial<IRynReplayBatchLimits> = {}) {
    this.limits = clampRynReplayBatchLimits(limits);
  }

  get size(): number {
    return this.buffer.length;
  }

  /**
   * Buffer one envelope. Returns a batch when a cap is reached, otherwise
   * undefined.
   *
   * A single envelope at or over the byte cap is returned as its own batch
   * rather than being dropped or held forever. rrweb full snapshots of a dense
   * Kendo grid genuinely can exceed a conservative byte cap on their own, and
   * an oversize event is still the most important event in the session — it is
   * the keyframe every later incremental event is a delta against. Dropping it
   * would leave the whole session unreplayable.
   */
  add(envelope: IRynReplayEnvelope): IRynReplayEnvelope[] | undefined {
    // Stamp the cumulative session index BEFORE sizing, so the byte count
    // includes the field and so it is fixed once and rides with the envelope
    // through every retry (#30110). First event of a session is 1.
    envelope.sessionEventCount = ++this.sessionEventCount;
    const bytes = rynReplayEnvelopeBytes(envelope);

    // Flush what is already buffered BEFORE admitting an event that would push
    // the batch over, so the oversize event travels alone and the earlier
    // events are not dragged over the cap with it.
    if (
      this.buffer.length > 0 &&
      this.bufferedBytes + bytes > this.limits.maxBytes
    ) {
      const ready = this.drain();
      this.buffer.push(envelope);
      this.bufferedBytes = bytes;
      return ready;
    }

    this.buffer.push(envelope);
    this.bufferedBytes += bytes;

    if (
      this.buffer.length >= this.limits.maxEvents ||
      this.bufferedBytes >= this.limits.maxBytes
    ) {
      return this.drain();
    }

    return undefined;
  }

  /** Take everything buffered. Returns an empty array when there is nothing. */
  drain(): IRynReplayEnvelope[] {
    if (this.buffer.length === 0) {
      return [];
    }
    const ready = this.buffer;
    this.buffer = [];
    this.bufferedBytes = 0;
    return ready;
  }
}
